import { SortConfig } from '../types';

export const formatAmount = (amount: string | number | null | undefined): string => {
  if (!amount || amount === '' || amount === '0') return '₹0';
  let amountToFormat = amount.toString().replace(/₹/g, '').replace(/\.00|,/g, '');
  if (isNaN(Number(amountToFormat)) || Number(amountToFormat) < 1000) return `₹${amountToFormat}`;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 2,
  }).format(Number(amountToFormat));
};

export const capitalizeString = (str: string | null | undefined): string =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  return new Intl.DateTimeFormat().format(new Date(date));
};

export const getStartOfWeek = (date: Date): Date => {
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + diff);
};

export const formatDateString = (date: Date): string => {
  const day = date.getDate();
  const suffix = ['th', 'st', 'nd', 'rd'][day % 10 > 3 || Math.floor(day / 10) === 1 ? 0 : day % 10];
  return `${day}${suffix}`;
};

export const getWeekOfYear = (date: Date): number => {
  const oneJan = new Date(date.getFullYear(), 0, 1);
  const numberOfDays = Math.floor((date.getTime() - oneJan.getTime()) / (24 * 60 * 60 * 1000));
  return Math.ceil((numberOfDays + oneJan.getDay() + 1) / 7);
};

export const getMonthFromYearWeek = (yearWeek: number, year?: number): number => {
  const currentYear = year ?? new Date().getFullYear();
  const oneJan = new Date(currentYear, 0, 1);
  const daysOffset = (yearWeek - 1) * 7;
  const weekDate = new Date(oneJan.getFullYear(), oneJan.getMonth(), oneJan.getDate() + daysOffset);
  return weekDate.getMonth() + 1;
};

export const getWeekOfMonthFromYearWeek = (yearWeek: number, year?: number): number => {
  const currentYear = year ?? new Date().getFullYear();
  const oneJan = new Date(currentYear, 0, 1);
  const daysOffset = (yearWeek - 1) * 7;
  const weekDate = new Date(oneJan.getFullYear(), oneJan.getMonth(), oneJan.getDate() + daysOffset);
  const startOfMonth = new Date(weekDate.getFullYear(), weekDate.getMonth(), 1);
  return Math.ceil((weekDate.getDate() + startOfMonth.getDay()) / 7);
};

export interface WeekData {
  week: string;
  weekNo: number;
  dates: string;
  yearWeek: number;
}

export const getWeeksData = (startDate: Date): WeekData[] => {
  return Array.from({ length: 4 }, (_, index) => {
    const weekStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + index * 7);
    const weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 4);
    return {
      week: `Week ${index + 1}`,
      weekNo: index + 1,
      dates: `${formatDateString(weekStart)} - ${formatDateString(weekEnd)}`,
      yearWeek: getWeekOfYear(weekStart),
    };
  });
};

export interface SortResult<T> {
  newConfig: SortConfig;
  sortedData: T[];
}

export const sortData = <T extends Record<string, any>>(
  data: T[],
  key: string,
  config: SortConfig
): SortResult<T> => {
  if (data.length === 0) return { newConfig: config, sortedData: data };
  let sortOrder = 1;
  if (config.key === key && config.direction === 'asc') sortOrder = -1;
  const newConfig: SortConfig = { key, direction: sortOrder === 1 ? 'asc' : 'desc' };
  const sortedData = [...data].sort((a, b) => {
    if (a[key] < b[key]) return -1 * sortOrder;
    if (a[key] > b[key]) return 1 * sortOrder;
    return 0;
  });
  return { newConfig, sortedData };
};

export const getStatusClassString = (status: string | null | undefined): string => {
  if (!status) return '';
  const statusString = status.toLowerCase();
  const statusClasses: Record<string, string> = {
    'approval pending': 'status-pending',
    'in process': 'status-in-process',
    candidate: 'status-new',
    approved: 'status-approved',
    accepted: 'status-approved',
    rejected: 'status-rejected',
    active: 'bg-green-100 text-green-800',
    closed: 'bg-red-100 text-red-800',
  };
  return statusClasses[statusString] || '';
};

export const getTotalTimeUsedTillNow = (
  startedAt: string | Date | null | undefined,
  initialUsedTime: string | null | undefined
): string => {
  if (!startedAt || !initialUsedTime) return '00:00';
  const startTime = new Date(startedAt);
  const currentTime = new Date();
  const workedForMs = currentTime.getTime() - startTime.getTime();
  const workedForHours = Math.floor(workedForMs / (1000 * 60 * 60));
  const workedForMinutes = Math.floor((workedForMs % (1000 * 60 * 60)) / (1000 * 60));
  const [prevHours, prevMinutes] = (initialUsedTime ?? '00:00').split(':').map(Number);
  const totalHours = workedForHours + prevHours;
  const totalMinutes = workedForMinutes + prevMinutes;
  const totalTimeUsedHours = totalHours + Math.floor(totalMinutes / 60);
  const totalTimeUsedMinutes = totalMinutes % 60;
  return `${totalTimeUsedHours.toString().padStart(2, '0')}:${totalTimeUsedMinutes.toString().padStart(2, '0')}`;
};
